import { addMinutes, compareAsc, differenceInSeconds, format } from "date-fns";
import { ColumnProps, HasDataTableValueChanged, IventisCellRendererParams, TextFilterComponent, TimeCellComponent, TimeCellValue } from "@iventis/data-table";
import { Content } from "@iventis/translations/content/typed-content";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { RelativeEventRuleRelationship } from "@iventis/domain-model/model/relativeEventRuleRelationship";
import { DataField } from "@iventis/domain-model/model/dataField";
import { TimeRelativeTo } from "@iventis/domain-model/model/timeRelativeTo";
import { RelativeEventRule } from "@iventis/domain-model/model/relativeEventRule";
import { utcToZonedTime } from "date-fns-tz";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import {
    convertDataFieldsToColumns,
    disableFilteringOnColumn,
    generateCheckboxColumn,
    generateListColumn,
    generateTextColumn,
    generateTimeColumn,
    getTimeCellValue,
} from "@iventis/data-table/helpers/column-generators";
import { ReplaySubject } from "rxjs";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import {
    hasObjectArrayChanged,
    hasDataFieldValueChanged,
    isValidDate,
    addTimeSpanToDate,
    invertZeroTimeSpan,
    timeDifference,
    ZERO_TIME_SPAN,
    timeSpanToTimeDifference,
    EMPTY_GUID,
} from "@iventis/utilities";
import { translate } from "@iventis/translations/translation";
import { findTopCalendarlevelEventBlock } from "./schedule-helper-functions";
import { RunSheetRowNode, RunSheetData, InsertRowPosition, RunSheetTemplateEntities } from "./run-sheet.types";
import {
    ActivityCellEditor,
    ActivityCellRenderer,
    LocationCellEditor,
    LocationCellRenderer,
    LocationFilter,
    RunSheetDurationCellEditor,
    RunSheetEndAtEditor,
    RunSheetStartAtEditor,
} from "./run-sheet-components";
import { TimeRuleParams } from "./time-rules.types";
import { generateDurationColumn, scheduleTableTimeValidator } from "./schedule-table.helpers";
import { hasRelativeRuleChanged } from "./time-rules.helpers";
import { findEventBlockHierarchyEntitiesByCalendarLevel, findHierarchyEntitiesByType, findHierarchyEntityById } from "./hierarchy.helpers";
import { getCalendarSessionColour, getEventBlockDisplayValuesFromDataFieldValues, getEventBlockCode } from "./event-block-helpers";
import { ScheduleMachineContext } from "./schedule.machine.types";

/** Any column id's that the backend has no interest in (used to prevent network requests e.g. patch) */
export const columnsBackendIgnores: (keyof RunSheetData)[] = ["sessionId"];

const timeRuleColumnProps = (scheduleDaysMap: Map<number, Date>, eventBlockTypes: ScheduleConfigEventBlockType[], editable: boolean): TimeRuleParams => ({
    field: "startAt",
    headerName: translate(Content.runsheet.headings.start),
    suppressMenu: true,
    sortable: true,
    comparator: (valueA, valueB) => compareAsc(valueA.value, valueB.value),
    editable,
    cellRenderer: TimeCellComponent,
    cellEditor: RunSheetStartAtEditor,
    cellEditorParams: { validator: scheduleTableTimeValidator(), getTimeCellValue, scheduleDaysMap, eventBlockTypes },
    ...disableFilteringOnColumn(),
});

/**
 *
 * @returns {ColumnProps[]} - column props to pass into data table
 */
export const generateColumnsForRunSheet = (
    machineContext: ScheduleMachineContext,
    visibleDataFields: DataField[],
    selectedVenueLocation: Hierarchy<VenueLocation>,
    getListItems: (id: string) => ReplaySubject<DataFieldListItem[]>,
    hasPermission: boolean
): ColumnProps[] => [
    // Hidden column's
    {
        agGridColumnProps: { field: "relativeEventRules", hide: true },
    },
    {
        agGridColumnProps: { field: "estimatedTime", hide: true },
    },
    {
        agGridColumnProps: { field: "rowColour", hide: true },
    },
    {
        agGridColumnProps: { field: "sessionId", hide: true },
    },
    // Start At column
    {
        agGridColumnProps: timeRuleColumnProps(machineContext.availableDays, machineContext.schedule.config.eventBlockTypes, hasPermission),
    },
    // Duration Column
    generateDurationColumn({ cellEditor: RunSheetDurationCellEditor, cellEditorPopup: false, editable: hasPermission }),
    // End At Column
    generateTimeColumn<keyof RunSheetData>({ headerName: translate(Content.runsheet.headings.end), field: "endAt" }, getTimeCellValue, {
        editable: hasPermission,
        cellEditor: RunSheetEndAtEditor,
        ...disableFilteringOnColumn(),
    }),
    // Time From Zero At Column
    generateTextColumn<keyof RunSheetData>(
        { headerName: translate(Content.runsheet.headings.zero), field: "timeFromZero" },
        {
            editable: false,
            valueGetter: ({ api, data }) => {
                // Each zero time nodes' timeFromZero value is 0
                if (data.id?.includes?.("ZERO")) {
                    return ZERO_TIME_SPAN;
                }
                // Each sessions' timeFromZero value is the difference between its' own startAt and zeroAt
                if (data.type?.calendarLevel === 1) {
                    return data.zeroAt != null ? timeSpanToTimeDifference(invertZeroTimeSpan(data.zeroAt)) : `-${ZERO_TIME_SPAN}`;
                }
                // If event has no session id, value is 0
                if (data?.sessionId == null) {
                    return ZERO_TIME_SPAN;
                }
                // The rest is the difference between its' own startAt and its' session's zeroAt (or startAt if zeroAt is null)
                let sessionZeroTime;
                api.forEachNode((node) => {
                    if (node.data.id === data.sessionId) {
                        sessionZeroTime =
                            node.data.zeroAt != null && node.data.startAt?.value != null ? addTimeSpanToDate(node.data.zeroAt, node.data.startAt?.value) : node.data.startAt?.value;
                    }
                });
                return timeDifference(data.startAt?.value, sessionZeroTime);
            },
            ...disableFilteringOnColumn(),
        }
    ),
    // Actitvy Column
    {
        agGridColumnProps: {
            field: "activity",
            headerName: translate(Content.runsheet.headings.activity),
            sortable: true,
            editable: hasPermission,
            maxWidth: 700,
            initialWidth: 500,
            suppressAutoSize: true,
            filter: TextFilterComponent,
            cellRenderer: ActivityCellRenderer,
            cellEditor: ActivityCellEditor,
            cellEditorPopup: false,
            cellEditorParams: { dataFields: machineContext.schedule.config.dataFields, scheduleVersionHierarchy: machineContext.selectedScheduleVersionHierarchy },
            menuTabs: ["filterMenuTab"],
            ...disableFilteringOnColumn(),
        },
    },
    // Location/Space Column
    generateListColumn<keyof RunSheetData>(
        {
            componentParams: {
                listItems: selectedVenueLocation.children.map(({ item }) => item),
            },
        },
        { headerName: translate(Content.table.schedule.space), field: "venueLocation" },
        {
            cellEditor: LocationCellEditor,
            cellEditorPopup: false,
            filter: LocationFilter,
            cellRenderer: LocationCellRenderer,
            editable: hasPermission,
        }
    ),
    // Done column
    ...(machineContext.schedule.config.dcasConfig.enableDone
        ? [
              generateCheckboxColumn(
                  { field: "done", headerName: translate(Content.runsheet.done) },
                  (params: IventisCellRendererParams<RunSheetData>) => params.node.data.type.name === RunSheetTemplateEntities.Zero
              ),
          ]
        : []),
    // Data field Columns
    ...convertDataFieldsToColumns(machineContext.schedule.config.dataFields, {
        hiddenDataFields: machineContext.schedule.config.dataFields.filter((df) => !visibleDataFields.some((visibleDf) => visibleDf.id === df.id)),
        orderedDataFields: visibleDataFields.map((df) => df.id),
        cellEditable: (row: RunSheetData, dataFieldId: string) => hasPermission && row.type.dataFields.some((df) => df.dataFieldId === dataFieldId),
        listItemGetter: getListItems,
    }),
];
/**
 *
 * @param {ScheduleMachineContext} context - schedule machine context
 * @returns Row data
 */
export const generateRowsForRunSheet = (
    machineContext: Pick<ScheduleMachineContext, "eventBlockHierarchy" | "eventBlocksDone" | "schedule">,
    runSheetVenueLocation: Hierarchy<VenueLocation>,
    timezone: string,
    hasPermission: boolean
): RunSheetRowNode["data"][] => {
    const {
        eventBlockHierarchy,
        eventBlocksDone,
        schedule: {
            config: { dataFields: dataFieldColumns, dcasConfig },
        },
    } = machineContext;

    const topLevelChildren = eventBlockHierarchy.children;
    // i.e. activities
    const nonCalendarEvents = findEventBlockHierarchyEntitiesByCalendarLevel(topLevelChildren, null);
    const groupedNonCalendarEvents = groupRepeatedEvents(nonCalendarEvents);

    const sessions = findEventBlockHierarchyEntitiesByCalendarLevel(topLevelChildren, 1).sort((a, b) => compareAsc(a.item.startAt, b.item.startAt));

    // Rows for entities found in the calendar (i.e. sessions and scheduled items)
    const calendarRows = sessions.flatMap((session) => {
        const sessionRows = createRowsFromSession(session.item, dataFieldColumns, runSheetVenueLocation, timezone, hasPermission);
        const subSessions = findEventBlockHierarchyEntitiesByCalendarLevel(session.children, 2);
        const subSessionRows = subSessions.map((subSession) =>
            rowFromEventBlock(subSession.item, dataFieldColumns, runSheetVenueLocation, session.item.id, timezone, hasPermission, {
                // eslint-disable-next-line no-underscore-dangle
                activity: getEventBlockDisplayValuesFromDataFieldValues(subSession.item.dataFieldValues, subSession.item.type.dataFields)?.displayText,
                linkedEventId: subSession.item.relativeEventRules?.[0]?.relativeToSpecificEventBlock?.id,
            })
        );

        const allSessionsCalendarRows = [...sessionRows, ...subSessionRows];
        return allSessionsCalendarRows;
    });

    // Rows for entities not found in the calendar (i.e. activities)
    const nonCalendarRows = Object.values(groupedNonCalendarEvents).flatMap((nonCalendarEvents) => {
        if (!(nonCalendarEvents?.length > 0)) {
            throw new Error("Id must be mapped to at least one event block");
        }
        const rowBlocks = [];

        // We can use the first item here as we know all of them have the same rules
        const { relativeEventRules } = nonCalendarEvents[0].item;

        const relationship = relativeEventRules?.length > 0 ? relativeEventRules[0].relationship : null;
        const relativeToEventBlockTypeId = relativeEventRules?.length > 0 ? relativeEventRules[0].relativeToEventBlockTypeId : null;

        // All event blocks in the hierarchy of the type specified in the events' relative rule
        const foundRelativeToEventBlocks = findHierarchyEntitiesByType(eventBlockHierarchy.children, relativeToEventBlockTypeId, (e) => e.type.id)?.sort((a, b) =>
            compareAsc(a.item.startAt, b.item.startAt)
        );

        nonCalendarEvents
            .sort((a, b) => compareAsc(a.item.startAt, b.item.startAt))
            .forEach((nonCalendarEvent, index) => {
                let relatedEventBlock: Hierarchy<EventBlock>;
                switch (relationship) {
                    case RelativeEventRuleRelationship.Every: {
                        // Since the activity occurs on Every session we must make sure the lengths are equal
                        if (nonCalendarEvents.length === foundRelativeToEventBlocks.length) {
                            relatedEventBlock = foundRelativeToEventBlocks[index];
                        }
                        break;
                    }
                    case RelativeEventRuleRelationship.FirstOfDay: {
                        // eslint-disable-next-line prefer-destructuring
                        relatedEventBlock = foundRelativeToEventBlocks[0];
                        break;
                    }
                    case RelativeEventRuleRelationship.LastOfDay: {
                        // eslint-disable-next-line prefer-destructuring
                        relatedEventBlock = foundRelativeToEventBlocks[foundRelativeToEventBlocks.length - 1];
                        break;
                    }
                    case RelativeEventRuleRelationship.Specific: {
                        const relativeToSpecificEventBlockId = relativeEventRules?.length > 0 ? relativeEventRules[0].relativeToSpecificEventBlock?.id : null;
                        relatedEventBlock = findHierarchyEntityById(eventBlockHierarchy.children, relativeToSpecificEventBlockId);
                        break;
                    }
                    default: {
                        relatedEventBlock = findClosestSession(
                            nonCalendarEvent.item.startAt,
                            relativeEventRules?.length > 0 ? foundRelativeToEventBlocks : sessions,
                            (session) => session.item.startAt
                        );
                        break;
                    }
                }

                rowBlocks.push(
                    rowFromEventBlock(
                        nonCalendarEvent.item,
                        dataFieldColumns,
                        runSheetVenueLocation,
                        findTopCalendarlevelEventBlock(relatedEventBlock, eventBlockHierarchy.children)?.item?.id,
                        timezone,
                        hasPermission,
                        relatedEventBlock != null
                            ? {
                                  linkedEventId: relatedEventBlock.item.id,
                              }
                            : {}
                    )
                );
            });
        return rowBlocks;
    });

    let rows: RunSheetData[] = [...calendarRows, ...nonCalendarRows];

    // sort the rows in order of start time, then event block type
    rows = rows.sort((a, b) => {
        const comp = compareAsc(a.startAt.value, b.startAt.value);
        if (comp === 0) {
            return compareEventBlockType(a.type, b.type);
        }
        return comp;
    });

    let indexTotal = 0;

    const index = (row: RunSheetData) => {
        if (shouldRunSheetRowBeIndexed({ data: row })) {
            indexTotal += 1;
            return indexTotal;
        }
        return null;
    };

    // Assign Activities indexes
    rows = rows.map((row) => {
        const newRow: RunSheetData = { ...row };
        newRow.index = index(row);
        if (dcasConfig.enableDone) {
            // If done is enabled, we must find any done entities matching the row's circumstances
            newRow.done = eventBlocksDone.some(
                (d) =>
                    d.eventBlockId === newRow.id &&
                    (newRow.sessionId == null || d.relativeEventBlockId === (newRow.relativeEventRules[0]?.relativeToSpecificEventBlock?.id ?? newRow.sessionId))
            );
        }
        return newRow;
    });

    return rows;
};

/** Determine the run sheet order from the type (if two rows have equal start time) */
const eventBlockTypeRunSheetOrder = (type: ScheduleConfigEventBlockType) => {
    if (type.calendarLevel === 1) {
        // Session priority
        return 0;
    }
    if (type.name === RunSheetTemplateEntities.Zero) {
        return 1;
    }
    if (type.calendarLevel === 2) {
        // Event unit priority
        return 2;
    }
    return 3;
};

const compareEventBlockType = (typeA: ScheduleConfigEventBlockType, typeB: ScheduleConfigEventBlockType): number =>
    Math.sign(eventBlockTypeRunSheetOrder(typeA) - eventBlockTypeRunSheetOrder(typeB));

export const shouldRunSheetRowBeIndexed = (row: { data: RunSheetData }) => {
    const eventBlockType = row.data?.type;
    switch (eventBlockType.calendarLevel) {
        case 1: {
            return false;
        }
        default: {
            // If it is not a session we can assume it is an indexed item
            return true;
        }
    }
};

export const getZeroRowId = (id: string) => `${id}-ZERO`;

export const isZeroRow = ({ id }: { id: string }) => id?.includes("ZERO");

const createRowsFromSession = (
    eventBlock: EventBlock,
    dataFieldColumns: DataField[],
    runSheetVenueLocation: Hierarchy<VenueLocation>,
    timezone: string,
    hasPermission: boolean
): RunSheetData[] => {
    const { zeroAt, startAt, id } = eventBlock;
    const zeroAtTime = zeroAt != null ? addTimeSpanToDate(zeroAt, startAt) : startAt;
    const sessionZeroAtDisplayText = format(utcToZonedTime(zeroAtTime, timezone), "HH:mm:ss");
    const sessionZeroAtValue = zeroAtTime;

    return [
        {
            ...rowFromEventBlock(eventBlock, dataFieldColumns, runSheetVenueLocation, id, timezone, hasPermission),
            activity: `${getActivity(eventBlock)} ${translate(Content.runsheet.activity.start)}`,
            zeroAt,
        },
        {
            id: getZeroRowId(eventBlock.id),
            startAt: { displayText: sessionZeroAtDisplayText, value: sessionZeroAtValue },
            endAt: { displayText: "", value: sessionZeroAtValue },
            estimatedTime: eventBlock.estimatedTime,
            date: { displayText: format(utcToZonedTime(sessionZeroAtValue, timezone), "dd/MM/yyyy"), value: sessionZeroAtValue },
            type: { name: RunSheetTemplateEntities.Zero } as ScheduleConfigEventBlockType,
            venueLocation: eventBlock.venueLocation,
            canEdit: false,
            shared: "",
            fa: "",
            activity: `${getActivity(eventBlock)} ${translate(Content.runsheet.activity.zero)}`,
            relativeEventRules: eventBlock.relativeEventRules,
            recurringEventRules: eventBlock.recurringEventRules,
            done: false,
            sessionId: id,
        },
    ];
};

export const rowFromEventBlock = (
    eventBlock: EventBlock,
    dataFieldColumns: DataField[],
    runSheetVenueLocation: Hierarchy<VenueLocation>,
    sessionId: string,
    timezone: string,
    hasPermission: boolean,
    overrides?: Partial<RunSheetData> // Override the zero time generated by its related session
): RunSheetData => ({
    id: eventBlock.id,
    startAt: { displayText: getDisplayTime(eventBlock.startAt, timezone), value: eventBlock.startAt },
    endAt: { displayText: getDisplayTime(eventBlock.endAt, timezone), value: eventBlock.endAt },
    date: { displayText: format(utcToZonedTime(eventBlock.startAt, timezone), "dd/MM/yyyy"), value: eventBlock.startAt },
    type: eventBlock.type,
    venueLocation: getRowVenueFromEventBlock(eventBlock, runSheetVenueLocation),
    canEdit: hasPermission,
    shared: "",
    fa: "",
    activity: getActivity(eventBlock),
    sessionId,
    relativeEventRules: eventBlock.relativeEventRules,
    recurringEventRules: eventBlock.recurringEventRules,
    estimatedTime: eventBlock.estimatedTime,
    rowColour: eventBlock.type.calendarLevel === 1 ? getCalendarSessionColour(eventBlock).fill : undefined,
    ...eventBlock.dataFieldValues.reduce((cum, { dataField, value }) => ({ ...cum, [dataField.id]: value }), {}),
    ...overrides,
});

/** Gets the run sheet venue location from a given event block */
const getRowVenueFromEventBlock = (eventBlock: EventBlock, runSheetVenueLocation: Hierarchy<VenueLocation>) => {
    if (eventBlock.type.calendarLevel == null && runSheetVenueLocation.item.id === eventBlock.venueLocation.id) {
        // If the activity has a venue location equal to the run sheet venue, we know it is not allocated to a space yet
        return null;
    }
    return eventBlock.venueLocation;
};

export const getDisplayTime = (date: Date, timezone: string) => (isValidDate(date) ? format(utcToZonedTime(date, timezone), "HH:mm:ss") : "");

const getActivity = (eventBlock: EventBlock): string => {
    switch (eventBlock.type.calendarLevel) {
        case 1:
            return getEventBlockCode(eventBlock) || "";
        case 2:
            return getEventBlockDisplayValuesFromDataFieldValues(eventBlock.dataFieldValues, eventBlock.type.dataFields).displayText || "";
        default:
            return eventBlock.name || "";
    }
};

// Takes index value and position and calculates new position
export const calculateRowIndex = (rowIndex: number, position: InsertRowPosition) => {
    switch (position) {
        case InsertRowPosition.ABOVE:
            return rowIndex;
        case InsertRowPosition.BELOW:
            return rowIndex + 1;
        default:
            throw new Error("Position was not recognised");
    }
};

// Constructs an empty run sheet data value
export const createNewActivity = (startAt: Date, endAt: Date, type: ScheduleConfigEventBlockType, sessionId: string, timezone: string, hasPermission: boolean): RunSheetData => {
    const endTime = type.calendarLevel === 2 ? addMinutes(endAt, 30) : endAt;
    return {
        id: null,
        new: true,
        startAt: { displayText: getDisplayTime(startAt, timezone), value: startAt },
        endAt: { displayText: getDisplayTime(endTime, timezone), value: endTime },
        date: { displayText: getDisplayTime(startAt, timezone), value: startAt },
        type,
        venueLocation: null,
        shared: "",
        activity: "",
        fa: "",
        canEdit: hasPermission,
        sessionId,
        relativeEventRules: [],
        recurringEventRules: [],
        done: false,
    };
};

export const findClosestSession = <TValue extends object>(time: Date, sessions: TValue[], getTime: (val: TValue) => Date): TValue => {
    let closestSession = null;
    let closestSessionDifferenceInSeconds;
    sessions.forEach((session) => {
        const currentDifferenceInSeconds = Math.abs(differenceInSeconds(time, getTime(session)));
        if (closestSessionDifferenceInSeconds == null || closestSessionDifferenceInSeconds > currentDifferenceInSeconds) {
            closestSessionDifferenceInSeconds = currentDifferenceInSeconds;
            closestSession = session;
        }
    });
    return closestSession;
};

/** Maps an id of an event block to an array of all it's repeated instances */
export const groupRepeatedEvents = (events: Hierarchy<EventBlock>[]) => {
    const mapping: Record<string, Hierarchy<EventBlock>[]> = {};
    events.forEach((event) => {
        const { item } = event;
        if (mapping[item.id]?.length > 0) {
            mapping[item.id].push(event);
        } else {
            mapping[item.id] = [event];
        }
    });
    return mapping;
};

/** Checks if we should be following the start time of a related event */
export const isRelativeToStartTime = ({ relativeEventRules }: { relativeEventRules: RelativeEventRule[] }) => relativeEventRules[0]?.relativeTo === TimeRelativeTo.StartTime;

/** Checks if we should be following the end time of a related event */
export const isRelativeToEndTime = ({ relativeEventRules }: { relativeEventRules: RelativeEventRule[] }) => relativeEventRules[0]?.relativeTo === TimeRelativeTo.EndTime;

/** Checks if we should be following the zero time of a related event */
export const isRelativeToZeroTime = ({ relativeEventRules }: { relativeEventRules: RelativeEventRule[] }) => relativeEventRules[0]?.relativeTo === TimeRelativeTo.ZeroTime;

export const hasATimeRule = (data: Pick<RunSheetData, "recurringEventRules" | "relativeEventRules">) =>
    (data.relativeEventRules && data.relativeEventRules[0]) || (data.recurringEventRules && data.recurringEventRules[0]);

export const hasRunSheetValueChanged = (dataFields: DataField[]): HasDataTableValueChanged<RunSheetData> => (val1, val2, colId) => {
    let hasChanged = false;
    switch (colId) {
        case "startAt":
        case "endAt": {
            if (compareAsc((val1 as TimeCellValue).value, (val2 as TimeCellValue).value) !== 0) {
                hasChanged = true;
            }
            break;
        }
        case "relativeEventRules": {
            if (hasObjectArrayChanged(val1 as RelativeEventRule[], val2 as RelativeEventRule[], hasRelativeRuleChanged)) {
                hasChanged = true;
            }
            break;
        }
        case "activity": {
            if (val1 !== val2) {
                hasChanged = true;
            }
            break;
        }
        case "venueLocation": {
            if ((val1 as VenueLocation)?.id !== (val2 as VenueLocation)?.id) {
                hasChanged = true;
            }
            break;
        }
        default: {
            // Check the column is a datafield and update accordingly
            const dataField = dataFields.find(({ id }) => id === colId);
            if (dataField != null) {
                if (hasDataFieldValueChanged(val1, val2, dataField.type)) {
                    hasChanged = true;
                }
            }
            break;
        }
    }
    return hasChanged;
};

export const eventBlockFromRow = (data: RunSheetData, allDataFields: DataField[], selectedVenueLocation: VenueLocation, scheduleVersionId: string): Hierarchy<EventBlock> => {
    const eventBlock: Hierarchy<EventBlock> = {
        item: ({
            startAt: data.startAt.value,
            endAt: data.endAt.value,
            type: data.type,
            // Event blocks not in calendar use their name for the activity column
            name: data.type?.calendarLevel == null ? data.activity : null,
            parentId: data.type?.allowedParentType === EMPTY_GUID ? null : data.sessionId,
            scheduleVersionId,
            // Event blocks not in calendar must default their venue to the run sheet venue
            venueLocation: data.type?.calendarLevel == null ? selectedVenueLocation : null,
            dataFieldValues: Object.entries(data).reduce((cum, [key, value]) => {
                const dataField = allDataFields.find((df) => df.id === key);
                if (dataField) {
                    cum.push({ dataField, value });
                }
                return cum;
            }, []),
        } as unknown) as EventBlock,
        children: [],
    };
    return eventBlock;
};
